import React, { useState, useEffect } from 'react';
import axios from 'axios';

function ClientsPage({ apiUrl, auth }) {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({ full_name: '', phone: '', balance: '', is_vip: false });

  // Recharge / details
  const [detailClient, setDetailClient] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [rechargeAmount, setRechargeAmount] = useState('');

  const headers = { Authorization: `Bearer ${auth.token}` };

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    try {
      const res = await axios.get(`${apiUrl}/clients`, { headers });
      setClients(res.data);
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const openAdd = () => {
    setEditClient(null);
    setForm({ full_name: '', phone: '', balance: '', is_vip: false });
    setShowModal(true);
  };

  const openEdit = (client) => {
    setEditClient(client);
    setForm({
      full_name: client.full_name,
      phone: client.phone,
      balance: '',
      is_vip: client.is_vip === 1,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.full_name || !form.phone) return;
    try {
      if (editClient) {
        await axios.put(`${apiUrl}/clients/${editClient.id}`, {
          full_name: form.full_name,
          phone: form.phone,
          is_vip: form.is_vip,
        }, { headers });
      } else {
        await axios.post(`${apiUrl}/clients`, {
          full_name: form.full_name,
          phone: form.phone,
          balance: form.is_vip ? 0 : parseFloat(form.balance || 0),
          is_vip: form.is_vip,
        }, { headers });
      }
      setShowModal(false);
      loadClients();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Supprimer le client "${client.full_name}" ?`)) return;
    try {
      await axios.delete(`${apiUrl}/clients/${client.id}`, { headers });
      loadClients();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const openDetails = async (client) => {
    setDetailClient(client);
    setRechargeAmount('');
    try {
      const res = await axios.get(`${apiUrl}/clients/${client.id}/transactions`, { headers });
      setTransactions(res.data);
    } catch (err) {
      setTransactions([]);
    }
  };

  const handleRecharge = async () => {
    const amount = parseFloat(rechargeAmount);
    if (!amount || amount <= 0) return;
    try {
      await axios.post(`${apiUrl}/clients/${detailClient.id}/recharge`, { amount }, { headers });
      const updated = { ...detailClient, balance: detailClient.balance + amount };
      setDetailClient(updated);
      setRechargeAmount('');
      loadClients();
      const res = await axios.get(`${apiUrl}/clients/${detailClient.id}/transactions`, { headers });
      setTransactions(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const filtered = clients.filter(c =>
    c.full_name.toLowerCase().includes(search.toLowerCase()) ||
    c.phone.includes(search) ||
    (c.barcode && c.barcode.includes(search))
  );

  const txLabel = (type) => {
    if (type === 'recharge') return 'Recharge';
    if (type === 'vip') return 'Consommation VIP';
    return 'Achat';
  };

  const txItems = (tx) => {
    if (tx.type === 'recharge') return '';
    try {
      const items = JSON.parse(tx.description);
      if (Array.isArray(items)) return items.map(i => `${i.name} x${i.qty || 1}`).join(', ');
    } catch (e) {}
    return tx.description || '';
  };

  return (
    <div>
      <div className="page-header">
        <h1>🧑‍🤝‍🧑 Gestion des clients</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Nouveau client</button>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher par nom, téléphone ou code-barres..."
          style={{
            width: '100%', maxWidth: '400px', padding: '10px 14px',
            borderRadius: '8px', border: '1px solid var(--border)', fontSize: '14px',
          }}
        />
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Téléphone</th>
              <th>Code-barres</th>
              <th>Solde</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(client => (
              <tr key={client.id}>
                <td style={{ fontWeight: '600' }}>{client.full_name}</td>
                <td>{client.phone}</td>
                <td style={{ fontFamily: 'monospace' }}>{client.barcode}</td>
                <td>
                  {client.is_vip === 1 ? (
                    <span style={{ color: 'var(--text-secondary)' }}>—</span>
                  ) : (
                    <span className="badge badge-success">{client.balance.toLocaleString()} DA</span>
                  )}
                </td>
                <td>
                  {client.is_vip === 1
                    ? <span className="badge badge-warning">⭐ VIP</span>
                    : <span className="badge badge-info">Standard</span>}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openDetails(client)}>👁 Détails</button>
                    <button className="btn btn-outline btn-sm" onClick={() => openEdit(client)}>✏️</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(client)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '24px' }}>Aucun client</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editClient ? `Modifier "${editClient.full_name}"` : 'Nouveau client'}</h2>
            <div className="form-group">
              <label>Nom complet</label>
              <input type="text" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} placeholder="Ex: Ahmed Benali" />
            </div>
            <div className="form-group">
              <label>Téléphone</label>
              <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Ex: 0555123456" />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.is_vip}
                  onChange={e => setForm({ ...form, is_vip: e.target.checked })}
                  style={{ width: '18px', height: '18px' }}
                />
                ⭐ Client VIP (consommation gratuite, sans solde)
              </label>
            </div>
            {!editClient && !form.is_vip && (
              <div className="form-group">
                <label>Solde initial (DA)</label>
                <input type="number" value={form.balance} onChange={e => setForm({ ...form, balance: e.target.value })} placeholder="Ex: 10000" />
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave}>{editClient ? 'Enregistrer' : 'Créer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Details modal */}
      {detailClient && (
        <div className="modal-overlay" onClick={() => setDetailClient(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <h2>{detailClient.full_name} {detailClient.is_vip === 1 && '⭐'}</h2>
            <div style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
              {detailClient.phone} — Code: {detailClient.barcode}
            </div>

            {detailClient.is_vip === 1 ? (
              <div style={{ background: '#FFF7ED', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', color: '#B45309', fontWeight: '600' }}>
                ⭐ Client VIP — consommation gratuite
              </div>
            ) : (
              <div style={{ background: '#F0FDF4', padding: '16px', borderRadius: '10px', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Solde actuel</div>
                <div style={{ fontSize: '26px', fontWeight: '800', color: '#059669' }}>{detailClient.balance.toLocaleString()} DA</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <input
                    type="number" value={rechargeAmount} onChange={e => setRechargeAmount(e.target.value)}
                    placeholder="Montant à recharger"
                    style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                  />
                  <button className="btn btn-primary" onClick={handleRecharge}>Recharger</button>
                </div>
              </div>
            )}

            <h3 style={{ fontSize: '15px', marginBottom: '8px' }}>Historique</h3>
            <div style={{ maxHeight: '280px', overflow: 'auto' }}>
              {transactions.length === 0 ? (
                <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '16px' }}>Aucune transaction</div>
              ) : (
                transactions.map(tx => (
                  <div key={tx.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 0', borderBottom: '1px solid var(--border)',
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{txLabel(tx.type)}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {txItems(tx)} {tx.created_at ? new Date(tx.created_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </div>
                    <div style={{
                      fontWeight: '700',
                      color: tx.type === 'recharge' ? '#059669' : tx.type === 'vip' ? '#B45309' : '#dc2626',
                    }}>
                      {tx.type === 'recharge' ? '+' : '-'}{tx.amount.toLocaleString()} DA
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modal-actions">
              <button className="btn btn-primary" onClick={() => setDetailClient(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientsPage;
